import IconComponent from "@mdi/react";
import { memo, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Clear } from "@material-ui/icons";
import { mdiChevronDown, mdiChevronRight } from "@mdi/js";
import { EMPTY_HOVER_LEGEND, useHoverLegendWriter } from "atoms/interaction";
import { setActiveFocus, updateGroupByTitle } from "store/features";
import { RootState } from "store/rootReducer";
import styled from "styled-components";

import { LegendItemModel } from "models/LegendItem";
import { EntityKind } from "models/entityKind";

import { Badge, BaseCheckboxInput } from "components/base";
import LegendColorPicker from "components/base/LegendColorPicker";

import "./LegendItem.scss";

export interface LegendItemLabel {
  text: string;
  count: Maybe<number>;
}

export interface LegendItemComponentModel {
  active?: boolean;
  allowChecked?: boolean;
  item: LegendItemModel;
  selected?: boolean;
  showOnlyTemplate?: boolean;
  showCounts?: boolean;
  template?: (item: LegendItemModel) => JSX.Element;
  onCheckChanged?: (checked, item) => void;
  onClick?: (event) => void;
  onClickLock?: (item: LegendItemModel) => void;
  onMouseEnter?: (item) => void;
  showGroupsNotInFilter?: boolean;
  fontSize?: number;
  itemStyle?: React.CSSProperties;
  titleStyle?: React.CSSProperties;
  isScreenshotOverlayVisible?: boolean;
  // When set, a chevron beside the checkbox toggles this row's expansion.
  onToggleExpanded?: () => void;
  isExpanded?: boolean;
  label?: Maybe<LegendItemLabel>;
  // rest of props
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

function LegendItem({
  active = false,
  allowChecked = false,
  item,
  selected = false,
  showOnlyTemplate = false,
  showCounts = true,
  template = null,
  onCheckChanged,
  onClick = null,
  onClickLock = null,
  showGroupsNotInFilter = true,
  itemStyle,
  titleStyle,
  colorPickerStyle,
  isScreenshotOverlayVisible = false,
  onToggleExpanded,
  isExpanded = false,
  label,
  ...rest
}: LegendItemComponentModel) {
  const dispatch = useDispatch();
  // Write hover state to Jotai atom (Phase 1 migration — replaces Redux groupBySlice.hoverLegendItem).
  const scheduleHoverWrite = useHoverLegendWriter();
  const [isSelected, setSelected] = useState(selected);
  // sync props with local state
  useEffect(() => {
    setSelected(selected);
  }, [selected]);

  const handleMouseEnter = useCallback(() => {
    if (isScreenshotOverlayVisible) return;

    scheduleHoverWrite({
      item: item.title,
      group: "",
      id: "",
      itemUnfiltered: "",
      isMultiProductChart: false
    });
    if (item.groupByTitle && item.groupByTitle.includes(EntityKind.Facility)) {
      dispatch(setActiveFocus(EntityKind.Facility));
    } else {
      dispatch(setActiveFocus(EntityKind.Well));
    }
    dispatch(updateGroupByTitle(item.groupByTitle));
  }, [
    dispatch,
    scheduleHoverWrite,
    item.title,
    item.groupByTitle,
    isScreenshotOverlayVisible
  ]);

  const handleMouseLeave = useCallback(() => {
    scheduleHoverWrite(EMPTY_HOVER_LEGEND);
  }, [scheduleHoverWrite]);

  const placeholderBinsEnabled = useSelector(
    (state: RootState) => state.groupBy.placeholderBinsEnabled
  );

  const shouldDisplay =
    item?.inFilter &&
    (item?.count !== 0 || (placeholderBinsEnabled ? showGroupsNotInFilter : false)) &&
    item?.color !== null;

  const count = label ? label.count : item.count;
  // Others can only be excluded, so its box works only to take the Exclude back off.
  const isCheckboxDisabled = item.disabled || (item.isOthersRollup && !item.isNotEqual);

  const pinnedColor = item.paletteColor
    ? { color: item.paletteColor, label: "Palette colour" }
    : undefined;

  return (
    <div
      className={`legend-item ${isSelected ? "selected" : ""} ${active ? "active" : ""} ${
        isScreenshotOverlayVisible ? "screenshot-mode" : ""
      }`}
      onClick={onClick}
      onPointerEnter={handleMouseEnter}
      onPointerLeave={handleMouseLeave}
      style={itemStyle}
      {...rest}>
      {template && template(item)}
      {allowChecked && (
        // Flex, so the box is centred instead of sitting on a text baseline that moves
        // when the red X replaces the tick.
        <CheckboxWrapper
          $othersRollup={item.isOthersRollup && !item.disabled}
          onClick={(e) => {
            // Stop click event from bubbling to parent div's onClick
            // This prevents checkbox clicks from triggering bin selection
            e.stopPropagation();
          }}>
          <BaseCheckboxInput
            value={item.isChecked}
            disabled={isCheckboxDisabled}
            checkIcon={
              item.isNotEqual ? (
                <Clear
                  className="icon check"
                  style={{
                    backfaceVisibility: "hidden",
                    display: "block",
                    flexShrink: 0,
                    fill: "white",
                    height: "18",
                    width: "18"
                  }}
                />
              ) : null
            }
            onChange={(val) => {
              if (isCheckboxDisabled) return;
              // Others can't be included, so a click on its red X only takes the Exclude off.
              const checked = item.isOthersRollup && item.isNotEqual ? false : val;
              onCheckChanged && onCheckChanged(checked, item);
            }}
          />
        </CheckboxWrapper>
      )}
      {shouldDisplay && (
        <LegendColorPicker
          values={{
            color: item.color,
            strokeColor: item.color,
            thickness: 0,
            opacity: 1
          }}
          onChange={(_, newColor) => {
            const newItem = {
              ...item,
              color: newColor
            };
            onClickLock?.(newItem);
          }}
          includeOpacity={false}
          showGroupsNotInFilter={showGroupsNotInFilter}
          onClickLock={onClickLock}
          item={item}
          colorPickerAdjustableSize={colorPickerStyle}
          pinnedColor={pinnedColor}
          isScreenshotOverlayVisible={isScreenshotOverlayVisible}
        />
      )}
      {allowChecked && onToggleExpanded && (
        <ExpandToggle
          type="button"
          $besideCheckbox={!shouldDisplay}
          data-testid="others-expand-toggle"
          aria-expanded={isExpanded}
          aria-label={isExpanded ? "Hide Others values" : "Show Others values"}
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpanded();
          }}>
          <IconComponent
            path={isExpanded ? mdiChevronDown : mdiChevronRight}
            size={1.6}
          />
        </ExpandToggle>
      )}
      {!showOnlyTemplate && (
        <TitleBadgeWrapper
          className={`${item.inFilter ? "" : "not-in-filter"}
          ${isSelected ? "selected" : ""}
          ${item.isChecked && !item.isNotEqual ? "is-checked" : ""}
          ${item.isNotEqual ? "is-not-equal" : ""}
        `}>
          <div
            className="legend-item-title"
            style={titleStyle}
            title={label?.text ?? item.title}>
            {/* The map screenshot legend is consistent with the chart legends and the focus legend is different (shows count separately) */}
            {label
              ? label.text
              : item.count != null && !item.isScreenShot
              ? item.value
              : showCounts
              ? item.text
              : item.value}
          </div>
          {count != null && showCounts && !item.isScreenShot && (
            <div className={`legend-item-count`}>
              <Badge>{count}</Badge>
            </div>
          )}
        </TitleBadgeWrapper>
      )}
    </div>
  );
}

// Others stays greyed out but keeps the pointer, so its right-click menu reads as reachable.
const CheckboxWrapper = styled.div<{ $othersRollup?: boolean }>`
  display: flex;
  ${({ $othersRollup }) =>
    $othersRollup && `.checkbox-input.disabled .input { cursor: pointer; }`}
`;

// 4px larger than BaseCheckboxInput, offset by negative margins so the row keeps its size.
// With no swatch before it, it also pulls in the glyph's own side padding.
const ExpandToggle = styled.button<{ $besideCheckbox?: boolean }>`
  flex-shrink: 0;
  width: 2.4rem;
  height: 2.4rem;
  margin: -0.2rem calc(var(--space-1) - 0.2rem);
  margin-left: ${({ $besideCheckbox }) =>
    $besideCheckbox ? "-0.3rem" : "calc(var(--space-1) - 0.2rem)"};
  padding: 0;
  border: none;
  background: none;
  color: var(--color-text);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TitleBadgeWrapper = styled.div`
  min-width: 0;
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  user-select: none;

  &.not-in-filter {
    .legend-item-title {
      color: #9b9b9b;
    }
    .Badge {
      color: #bbb;
    }
  }
  &.is-checked {
    font-weight: 700;
  }
  &.selected {
    .legend-item-title {
      color: #1e2029;
    }
  }
  &.is-not-equal {
    .legend-item-title,
    .Badge {
      color: #ff2855;
    }
  }
`;

export default memo(LegendItem);
